import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatUI.css';
import DebateEngagementNotice from '../DebateEngagementNotice';
import {
  TOTAL_GAME_ROUNDS,
  ROUNDS_PER_CHAT,
  INITIAL_COINS_PER_ROUND,
  DEBATE_TOPIC_TITLE,
  CHAT_FIRST,
  CHAT_DURATION_SECONDS,
} from '../../../gameConfig';
import { playDing } from '../../../utils/playDing';
import userAvatar from '../../../assets/user.png';
import partnerAvatar from '../../../assets/partner.png';

type MessagePayload = {
  content: string;
  senderID: string;
  messageID: string;
  timestamp: string;
  typingStartedAt?: string;
  typingEndedAt?: string;
  typingDurationMs?: number;
  keystrokeCount?: number;
  pasteAttemptCount?: number;
}

type TypingBout = {
  startedAtMs: number;
  lastKeystrokeAtMs: number;
  keystrokeCount: number;
  pasteAttemptCount: number;
}

type TypingComposition = {
  startedAtMs: number;
  keystrokeCount: number;
  pasteAttemptCount: number;
}

type TypingSessionEndReason = 'idle' | 'send' | 'clear' | 'lock' | 'unmount' | 'paste_blocked';

type DisplayMessage = {
  text: string;
  sender: 'user' | 'other';
  id: string;
};

/** Separator shown between chat sessions (e.g. "Round 6–10") */
type RoundSeparator = {
  type: 'roundSeparator';
  roundStart: number;
  roundEnd: number;
  id: string;
};

type ChatListItem = DisplayMessage | RoundSeparator;

function isDisplayMessage(item: ChatListItem): item is DisplayMessage {
  return !('type' in item) || (item as RoundSeparator).type !== 'roundSeparator';
}

type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

/** Client-side ping interval to keep the WebSocket alive through reverse proxies (e.g. Nginx 60s default). */
const CLIENT_PING_INTERVAL_MS = 25_000;
/** Throttle typing pulses so the server can treat either partner as active. */
const TYPING_SEND_INTERVAL_MS = 2_000;
/** Draft text alone does not count as typing after this long without a keystroke (align with backend `TYPING_ACTIVE_MS`). */
const LOCAL_TYPING_ACTIVE_MS = 5_000;
/** Hide partner typing bubble if no new pulse arrives within this window. */
const PARTNER_TYPING_HIDE_MS = 5_000;
/** Show a gentle nudge in the message area after this long with no messages or typing. */
const CHAT_IDLE_PROMPT_MS = 20_000;
/** Max reconnect attempts before giving up. */
const MAX_RECONNECT_ATTEMPTS = 50;
/** Base delay for exponential backoff (ms). */
const RECONNECT_BASE_DELAY_MS = 1000;
/** Max delay cap for exponential backoff (ms). */
const RECONNECT_MAX_DELAY_MS = 10_000;

interface ChatUIProps {
  currentUserProlificID: string | null;
  isLocked?: boolean;
  chatNumber?: number;
  currentRound?: number;
  debateTopicTitle?: string;
  onPartnerDisconnected?: () => void;
  /** Full debate layout: wide panel spanning former game-play + chat width */
  isDebateLayout?: boolean;
}

const SUBTOPIC_NEW_BADGE_MS = 6000;

type SubtopicPayload = {
  umbrellaTopic?: unknown;
  subtopic?: unknown;
  subtopics?: unknown;
  revision?: unknown;
  isNew?: unknown;
};

function extractSubtopic(payload: SubtopicPayload): string | null {
  if (typeof payload.subtopic === 'string' && payload.subtopic.trim().length > 0) {
    return payload.subtopic.trim();
  }
  if (Array.isArray(payload.subtopics)) {
    const first = payload.subtopics.find(
      (s): s is string => typeof s === 'string' && s.trim().length > 0
    );
    if (first) return first.trim();
  }
  return null;
}

// Helper function to get initial tokens for a round (matches backend logic)
function getInitialTokensForRound(roundIndex: number): number {
  if (roundIndex < 0 || roundIndex >= TOTAL_GAME_ROUNDS) return INITIAL_COINS_PER_ROUND;
  return INITIAL_COINS_PER_ROUND;
}

const ChatUI: React.FC<ChatUIProps> = ({
  currentUserProlificID,
  isLocked = false,
  chatNumber,
  currentRound = 0,
  debateTopicTitle,
  onPartnerDisconnected,
  isDebateLayout = false,
}) => {
  const [serverUmbrellaTopic, setServerUmbrellaTopic] = useState<string | null>(null);
  const propUmbrellaTopic =
    typeof debateTopicTitle === 'string' && debateTopicTitle.trim()
      ? debateTopicTitle.trim()
      : null;
  const chatUmbrellaTopic = serverUmbrellaTopic || propUmbrellaTopic || DEBATE_TOPIC_TITLE;
  // Chat-first: debate unlocks immediately when the main game starts.
  const currentRoundNumber = currentRound + 1; // 1-indexed
  const nextChatThemeTitle = chatUmbrellaTopic;
  const debateMinutes = Math.round(CHAT_DURATION_SECONDS / 60);

  const inChatPhase = chatNumber != null;

  const discussionAvailabilityLabel = (() => {
    if (CHAT_FIRST) {
      if (!inChatPhase) {
        return <>Discussion is over - investment round in progress.</>;
      }
      if (isLocked) {
        return (
          <>
            Answer the pre-discussion question to start your <strong>{debateMinutes}-minute discussion</strong>.
          </>
        );
      }
      return <>Discussion in progress — share your honest perspective!</>;
    }

    // --- Previous layout: discussion after ROUNDS_PER_CHAT investment rounds ---
    const isFinalRoundAfterDebate = currentRoundNumber > ROUNDS_PER_CHAT;
    if (isFinalRoundAfterDebate) {
      return <>Discussion is over!</>;
    }
    if (currentRoundNumber === ROUNDS_PER_CHAT) {
      return <>Discussion will be available <strong>at the end of this round</strong></>;
    }
    const roundsUntilDiscussion = ROUNDS_PER_CHAT - currentRoundNumber + 1;
    return (
      <>
        Discussion will be available <strong>in {roundsUntilDiscussion} rounds</strong>
      </>
    );
  })();

  const showUpcomingDebateTheme = isLocked && !!nextChatThemeTitle;
  
  // (Legacy) Calculate next rounds and their initial tokens (not currently shown in the UI)
  const nextRoundStart = currentRound + 1; // Next round index (0-based)
  const nextRounds = Array.from({ length: ROUNDS_PER_CHAT }, (_, i) => {
    const roundIndex = nextRoundStart + i;
    if (roundIndex >= TOTAL_GAME_ROUNDS) return null; // Beyond game end
    return {
      roundNumber: roundIndex + 1, // 1-indexed for display
      roundIndex: roundIndex,
      initialTokens: getInitialTokensForRound(roundIndex)
    };
  }).filter(r => r !== null) as Array<{ roundNumber: number; roundIndex: number; initialTokens: number }>;
  const [chatItems, setChatItems] = useState<ChatListItem[]>([]);
  const [currentSubtopic, setCurrentSubtopic] = useState<string | null>(null);
  const [subtopicRevision, setSubtopicRevision] = useState(0);
  const [showSubtopicUpdated, setShowSubtopicUpdated] = useState(false);
  const [input, setInput] = useState('');
  const [partnerIsTyping, setPartnerIsTyping] = useState(false);
  const [userIsActivelyTyping, setUserIsActivelyTyping] = useState(false);
  const [showIdlePrompt, setShowIdlePrompt] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const previousChatNumberRef = useRef<number | undefined>(chatNumber);

  const [isChatLocked, setIsChatLocked] = useState(false);
  const [roundsToUnlock, setRoundsToUnlock] = useState(2);

  // --- Reconnection & connection health state ---
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const reconnectAttemptRef = useRef<number>(0);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingMessagesRef = useRef<string[]>([]);
  const lastTypingSentRef = useRef(0);
  const partnerTypingHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const localTypingExpireTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastLocalInputAtRef = useRef(0);
  const inputValueRef = useRef('');
  const typingBoutRef = useRef<TypingBout | null>(null);
  const compositionRef = useRef<TypingComposition | null>(null);
  const chatIdleTimerRef = useRef<NodeJS.Timeout | null>(null);
  /** True when the cleanup function has run — prevents reconnect after unmount. */
  const unmountedRef = useRef<boolean>(false);
  /** Stable ref for onPartnerDisconnected to avoid reconnect churn. */
  const onPartnerDisconnectedRef = useRef(onPartnerDisconnected);
  const prevLockedRef = useRef(isLocked);
  const subtopicBadgeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const subtopicRevisionRef = useRef(0);
  const lastNotifiedRevisionRef = useRef(0);
  const currentSubtopicRef = useRef<string | null>(null);
  useEffect(() => { onPartnerDisconnectedRef.current = onPartnerDisconnected; }, [onPartnerDisconnected]);

  const applySubtopicUpdate = useCallback((payload: SubtopicPayload) => {
    if (typeof payload.umbrellaTopic === 'string' && payload.umbrellaTopic.trim()) {
      setServerUmbrellaTopic(payload.umbrellaTopic.trim());
    }

    const nextSubtopic = extractSubtopic(payload);
    if (!nextSubtopic) return;

    const nextRevision =
      typeof payload.revision === 'number' && Number.isFinite(payload.revision)
        ? payload.revision
        : subtopicRevisionRef.current + 1;

    const prevSubtopic = currentSubtopicRef.current;
    const sameText =
      prevSubtopic != null &&
      prevSubtopic.trim().toLowerCase() === nextSubtopic.trim().toLowerCase();
    if (nextRevision <= subtopicRevisionRef.current && sameText) return;

    const isFreshUpdate = nextRevision > lastNotifiedRevisionRef.current;

    currentSubtopicRef.current = nextSubtopic;
    subtopicRevisionRef.current = nextRevision;
    setCurrentSubtopic(nextSubtopic);
    setSubtopicRevision(nextRevision);
    if (isFreshUpdate) {
      lastNotifiedRevisionRef.current = nextRevision;
      setShowSubtopicUpdated(true);
      playDing();

      if (subtopicBadgeTimerRef.current) {
        clearTimeout(subtopicBadgeTimerRef.current);
      }
      subtopicBadgeTimerRef.current = setTimeout(() => {
        setShowSubtopicUpdated(false);
        subtopicBadgeTimerRef.current = null;
      }, SUBTOPIC_NEW_BADGE_MS);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const persistTypingSession = useCallback((payload: Record<string, unknown>) => {
    const json = JSON.stringify(payload);
    const socket = ws.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(json);
        return;
      } catch (e) {
        console.warn('ChatUI: Failed to send typing session over WebSocket', e);
      }
    }

    const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
    if (!apiBaseUrl || !currentUserProlificID) return;
    try {
      fetch(`${apiBaseUrl}/messages/typing-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': currentUserProlificID,
        },
        body: json,
        keepalive: true,
      }).catch((err) => {
        console.warn('ChatUI: Failed to persist typing session', err);
      });
    } catch (err) {
      console.warn('ChatUI: Failed to persist typing session', err);
    }
  }, [currentUserProlificID]);

  const flushTypingBout = useCallback((endedReason: TypingSessionEndReason, extra: { messageID?: string; draftLength?: number } = {}) => {
    const bout = typingBoutRef.current;
    typingBoutRef.current = null;
    if (!bout || !currentUserProlificID) return;

    const endedAtMs = Date.now();
    persistTypingSession({
      type: 'typing_session',
      senderID: currentUserProlificID,
      startedAt: new Date(bout.startedAtMs).toISOString(),
      endedAt: new Date(endedAtMs).toISOString(),
      durationMs: Math.max(0, endedAtMs - bout.startedAtMs),
      keystrokeCount: bout.keystrokeCount,
      draftLength: extra.draftLength ?? inputValueRef.current.length,
      pasteAttemptCount: bout.pasteAttemptCount,
      endedReason,
      messageID: extra.messageID || null,
    });
  }, [currentUserProlificID, persistTypingSession]);

  const flushTypingBoutRef = useRef(flushTypingBout);
  flushTypingBoutRef.current = flushTypingBout;

  const resetLocalTypingIndicator = useCallback(() => {
    if (localTypingExpireTimerRef.current) {
      clearTimeout(localTypingExpireTimerRef.current);
      localTypingExpireTimerRef.current = null;
    }
    lastLocalInputAtRef.current = 0;
    setUserIsActivelyTyping(false);
  }, []);

  const clearLocalTypingActivity = useCallback((endedReason: TypingSessionEndReason = 'clear') => {
    if (endedReason === 'send') {
      flushTypingBout('send');
    } else {
      flushTypingBout(endedReason);
      compositionRef.current = null;
    }
    resetLocalTypingIndicator();
  }, [flushTypingBout, resetLocalTypingIndicator]);

  const lastPasteBlockAtRef = useRef(0);
  const notePasteAttempt = useCallback(() => {
    const now = Date.now();
    if (now - lastPasteBlockAtRef.current < 250) return;
    lastPasteBlockAtRef.current = now;

    const composition = compositionRef.current;
    if (composition) {
      composition.pasteAttemptCount += 1;
    }
    const bout = typingBoutRef.current;
    if (bout) {
      bout.pasteAttemptCount += 1;
    } else if (currentUserProlificID) {
      persistTypingSession({
        type: 'typing_session',
        senderID: currentUserProlificID,
        startedAt: new Date().toISOString(),
        endedAt: new Date().toISOString(),
        durationMs: 0,
        keystrokeCount: 0,
        draftLength: inputValueRef.current.length,
        pasteAttemptCount: 1,
        endedReason: 'paste_blocked',
        messageID: null,
      });
    }
  }, [currentUserProlificID, persistTypingSession]);

  const bumpLocalTypingActivity = useCallback((isKeystroke = false) => {
    const now = Date.now();
    lastLocalInputAtRef.current = now;

    if (!compositionRef.current) {
      compositionRef.current = {
        startedAtMs: now,
        keystrokeCount: 0,
        pasteAttemptCount: 0,
      };
    }
    if (isKeystroke) {
      compositionRef.current.keystrokeCount += 1;
    }

    if (!typingBoutRef.current) {
      typingBoutRef.current = {
        startedAtMs: now,
        lastKeystrokeAtMs: now,
        keystrokeCount: 0,
        pasteAttemptCount: 0,
      };
    } else {
      typingBoutRef.current.lastKeystrokeAtMs = now;
    }
    if (isKeystroke) {
      typingBoutRef.current.keystrokeCount += 1;
    }

    setUserIsActivelyTyping(true);
    if (localTypingExpireTimerRef.current) {
      clearTimeout(localTypingExpireTimerRef.current);
    }
    localTypingExpireTimerRef.current = setTimeout(() => {
      if (Date.now() - lastLocalInputAtRef.current >= LOCAL_TYPING_ACTIVE_MS) {
        setUserIsActivelyTyping(false);
        flushTypingBout('idle');
      }
      localTypingExpireTimerRef.current = null;
    }, LOCAL_TYPING_ACTIVE_MS);
  }, [flushTypingBout]);

  const clearPartnerTypingIndicator = useCallback(() => {
    if (partnerTypingHideTimerRef.current) {
      clearTimeout(partnerTypingHideTimerRef.current);
      partnerTypingHideTimerRef.current = null;
    }
    setPartnerIsTyping(false);
  }, []);

  const showPartnerTypingIndicator = useCallback(() => {
    setPartnerIsTyping(true);
    if (partnerTypingHideTimerRef.current) {
      clearTimeout(partnerTypingHideTimerRef.current);
    }
    partnerTypingHideTimerRef.current = setTimeout(() => {
      partnerTypingHideTimerRef.current = null;
      setPartnerIsTyping(false);
    }, PARTNER_TYPING_HIDE_MS);
  }, []);

  const showPartnerTypingIndicatorRef = useRef(showPartnerTypingIndicator);
  const clearPartnerTypingIndicatorRef = useRef(clearPartnerTypingIndicator);
  useEffect(() => {
    showPartnerTypingIndicatorRef.current = showPartnerTypingIndicator;
  }, [showPartnerTypingIndicator]);
  useEffect(() => {
    clearPartnerTypingIndicatorRef.current = clearPartnerTypingIndicator;
  }, [clearPartnerTypingIndicator]);

  useEffect(scrollToBottom, [chatItems, partnerIsTyping]);

  // Gentle nudge when no chat messages have been sent for a while (typing does not affect this).
  useEffect(() => {
    if (chatIdleTimerRef.current) {
      clearTimeout(chatIdleTimerRef.current);
      chatIdleTimerRef.current = null;
    }

    if (isLocked) {
      setShowIdlePrompt(false);
      return;
    }

    setShowIdlePrompt(false);

    chatIdleTimerRef.current = setTimeout(() => {
      setShowIdlePrompt(true);
      chatIdleTimerRef.current = null;
    }, CHAT_IDLE_PROMPT_MS);

    return () => {
      if (chatIdleTimerRef.current) {
        clearTimeout(chatIdleTimerRef.current);
        chatIdleTimerRef.current = null;
      }
    };
  }, [chatItems, isLocked]);

  useEffect(() => {
    const wasLocked = prevLockedRef.current;
    prevLockedRef.current = isLocked;
    if (wasLocked && !isLocked) {
      setCurrentSubtopic(null);
      currentSubtopicRef.current = null;
      setSubtopicRevision(0);
      subtopicRevisionRef.current = 0;
      lastNotifiedRevisionRef.current = 0;
      setServerUmbrellaTopic(null);
      setShowSubtopicUpdated(false);
      clearPartnerTypingIndicator();
      clearLocalTypingActivity();
      if (subtopicBadgeTimerRef.current) {
        clearTimeout(subtopicBadgeTimerRef.current);
        subtopicBadgeTimerRef.current = null;
      }
    }
  }, [isLocked, chatNumber, clearPartnerTypingIndicator, clearLocalTypingActivity]);

  useEffect(() => {
    if (isLocked) {
      clearPartnerTypingIndicator();
      clearLocalTypingActivity('lock');
    }
  }, [isLocked, clearPartnerTypingIndicator, clearLocalTypingActivity]);

  // Restore persisted chat messages from the database when discussion unlocks.
  useEffect(() => {
    if (isLocked || !currentUserProlificID) return;

    const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
    if (!apiBaseUrl) return;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/messages/history`, {
          headers: { 'prolific-id': currentUserProlificID },
        });
        if (!res.ok || cancelled) return;

        const data = await res.json();
        if (!Array.isArray(data.messages) || data.messages.length === 0 || cancelled) return;

        setChatItems((prev) => {
          const existingIds = new Set(
            prev.filter(isDisplayMessage).map((item) => item.id)
          );
          const restored = data.messages
            .filter((msg: { messageID?: string; id?: number }) => {
              const id = msg.messageID || String(msg.id);
              return !existingIds.has(id);
            })
            .map((msg: { content: string; senderID: string; messageID?: string; id?: number }) => ({
              text: msg.content,
              sender: (msg.senderID === currentUserProlificID ? 'user' : 'other') as 'user' | 'other',
              id: msg.messageID || String(msg.id),
            }));

          if (restored.length === 0) return prev;
          return [...prev, ...restored];
        });
      } catch (err) {
        console.warn('ChatUI: Failed to load message history', err);
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [isLocked, currentUserProlificID]);

  useEffect(() => {
    const flushOnLeave = () => {
      flushTypingBoutRef.current('unmount');
    };
    window.addEventListener('pagehide', flushOnLeave);
    return () => {
      window.removeEventListener('pagehide', flushOnLeave);
      if (partnerTypingHideTimerRef.current) {
        clearTimeout(partnerTypingHideTimerRef.current);
      }
      if (chatIdleTimerRef.current) {
        clearTimeout(chatIdleTimerRef.current);
      }
      if (localTypingExpireTimerRef.current) {
        clearTimeout(localTypingExpireTimerRef.current);
      }
      flushTypingBoutRef.current('unmount');
    };
  }, []);

  useEffect(() => {
    console.log("Chat history (items):", chatItems.length);
  }, [chatItems]);

  // Do NOT clear chat when session ends – keep full history. When a new chat session starts, add a round separator.
  // Only update previousChatNumberRef when in chat (chatNumber defined), so we don't clear it when leaving chat.
  useEffect(() => {
    if (chatNumber !== undefined) {
      const prev = previousChatNumberRef.current;
      // Add separator when entering chat 2+ (either we're new to this chat, or we advanced from a lower chat number)
      if (chatNumber > 1 && (prev === undefined || chatNumber > prev)) {
        const roundStart = (chatNumber - 1) * ROUNDS_PER_CHAT + 1;
        const roundEnd = chatNumber * ROUNDS_PER_CHAT;
        setChatItems((prevItems) => [
          ...prevItems,
          { type: 'roundSeparator', roundStart, roundEnd, id: `round-sep-${chatNumber}` },
        ]);
      }
      previousChatNumberRef.current = chatNumber;
    }
  }, [chatNumber]);

  // --- Build WebSocket URL once ---
  const getWsUrl = useCallback(() => {
    if (!currentUserProlificID) return null;
    const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
    const wsProtocol = apiBaseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsBase = apiBaseUrl.replace(/^https?/, wsProtocol);
    return `${wsBase}/messages?prolificID=${encodeURIComponent(currentUserProlificID)}`;
  }, [currentUserProlificID]);

  // --- Start client-side ping to keep the connection alive through reverse proxies ---
  const startPing = useCallback((socket: WebSocket) => {
    // Clear any previous ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    pingIntervalRef.current = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'ping' }));
        } catch (e) {
          console.warn('ChatUI: Failed to send client ping', e);
        }
      }
    }, CLIENT_PING_INTERVAL_MS);
  }, []);

  // --- Flush any messages that were queued while disconnected ---
  const flushPendingMessages = useCallback((socket: WebSocket) => {
    while (pendingMessagesRef.current.length > 0) {
      const msg = pendingMessagesRef.current.shift()!;
      try {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(msg);
          console.log('ChatUI: Flushed pending message');
        } else {
          // Put it back and stop flushing
          pendingMessagesRef.current.unshift(msg);
          break;
        }
      } catch (e) {
        console.error('ChatUI: Error flushing pending message', e);
        pendingMessagesRef.current.unshift(msg);
        break;
      }
    }
  }, []);

  // --- Core connect function with reconnect logic ---
  const connect = useCallback(() => {
    const wsURL = getWsUrl();
    if (!wsURL || unmountedRef.current) return;

    // Avoid opening a second socket while one is still connecting or open.
    if (ws.current?.readyState === WebSocket.CONNECTING || ws.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Tear down any stale closed socket and detach handlers so onclose cannot trigger another reconnect.
    if (ws.current) {
      const stale = ws.current;
      ws.current = null;
      stale.onopen = null;
      stale.onmessage = null;
      stale.onerror = null;
      stale.onclose = null;
      if (stale.readyState === WebSocket.OPEN || stale.readyState === WebSocket.CONNECTING) {
        stale.close(1000, 'Replacing connection');
      }
    }

    const isReconnect = reconnectAttemptRef.current > 0;
    setConnectionStatus(isReconnect ? 'reconnecting' : 'connecting');
    console.log(`ChatUI: ${isReconnect ? 'Reconnecting' : 'Connecting'} (attempt ${reconnectAttemptRef.current})...`);

    const socket = new WebSocket(wsURL);
    ws.current = socket;

    socket.onopen = () => {
      if (unmountedRef.current) { socket.close(); return; }
      console.log(`WebSocket connection opened for ${currentUserProlificID}: ${wsURL}`);
      setConnectionStatus('connected');
      reconnectAttemptRef.current = 0;

      // Start client-side ping keepalive
      startPing(socket);

      // Flush any messages queued while disconnected
      flushPendingMessages(socket);
    };

    socket.onmessage = (event) => {
      try {
        const receivedMessage = JSON.parse(event.data as string);

        // Ignore pong / ack responses
        if (receivedMessage.type === 'pong' || receivedMessage.type === 'ack_delivered') {
          return;
        }

        if (receivedMessage.type === 'player_disconnected') {
          onPartnerDisconnectedRef.current?.();
          return;
        }

        if (receivedMessage.type === 'debate_subtopics') {
          applySubtopicUpdate(receivedMessage);
          return;
        }

        if (receivedMessage.type === 'typing') {
          if (
            receivedMessage.senderID &&
            receivedMessage.senderID !== currentUserProlificID
          ) {
            showPartnerTypingIndicatorRef.current();
          }
          return;
        }

        if (!receivedMessage.content || !receivedMessage.messageID || !receivedMessage.senderID) {
          console.warn("Ignoring malformed message:", receivedMessage);
          return;
        }

        const messageSenderType = receivedMessage.senderID === currentUserProlificID ? 'user' : 'other';

        if (messageSenderType === 'other') {
          clearPartnerTypingIndicatorRef.current();
        }

        setChatItems((prev) => [
          ...prev,
          {
            text: receivedMessage.content,
            sender: messageSenderType,
            id: receivedMessage.messageID || new Date().toISOString() + Math.random(),
          },
        ]);
      } catch (error) {
        console.error(`Error parsing incoming message for ${currentUserProlificID}:`, error);
      }
    };

    socket.onclose = (event) => {
      if (unmountedRef.current) return;
      console.log(`WebSocket connection closed for ${currentUserProlificID}`, event.code, event.reason);

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Code 1000 = normal close (component unmount / intentional), don't reconnect.
      // Code 1008 = policy violation (server rejected: player not found, no ID) — don't reconnect.
      if (event.code === 1000 || event.code === 1008) {
        setConnectionStatus('disconnected');
        return;
      }

      // Attempt reconnect with exponential backoff
      if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptRef.current += 1;
        const delay = Math.min(
          RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptRef.current - 1),
          RECONNECT_MAX_DELAY_MS
        );
        console.log(`ChatUI: Will reconnect in ${delay}ms (attempt ${reconnectAttemptRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
        setConnectionStatus('reconnecting');
        reconnectTimerRef.current = setTimeout(() => {
          if (!unmountedRef.current) {
            connect();
          }
        }, delay);
      } else {
        console.error(`ChatUI: Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`);
        setConnectionStatus('disconnected');
      }
    };

    socket.onerror = (error) => {
      console.error(`WebSocket error for ${currentUserProlificID}:`, error);
      // onclose will fire after this and handle reconnection
    };
  }, [currentUserProlificID, getWsUrl, startPing, flushPendingMessages]);

  const closeWebSocket = useCallback((reason: string) => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (ws.current) {
      const socket = ws.current;
      ws.current = null;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        console.log(`Closing WebSocket connection for ${currentUserProlificID}: ${reason}`);
        socket.close(1000, reason);
      }
    }
  }, [currentUserProlificID]);

  // --- Main WebSocket lifecycle effect ---
  useEffect(() => {
    unmountedRef.current = false;

    if (!currentUserProlificID) {
      console.log("ChatUI: Prolific ID prop not available or empty. WebSocket will not connect.");
      closeWebSocket('No prolific ID');
      setChatItems([]);
      return;
    }

    // Only maintain a live socket while chat is unlocked; Game/index owns the socket during rounds.
    if (isLocked) {
      console.log("ChatUI: Chat locked — WebSocket will not connect.");
      reconnectAttemptRef.current = 0;
      closeWebSocket('Chat locked');
      setConnectionStatus('disconnected');
      return;
    }

    // Reset reconnection state for a fresh connection
    reconnectAttemptRef.current = 0;
    pendingMessagesRef.current = [];

    connect();

    return () => {
      unmountedRef.current = true;
      closeWebSocket('Component unmount');
    };
  // Only reconnect when prolificID or lock state changes — onPartnerDisconnected is handled via ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserProlificID, isLocked]);

  const sendTypingIndicator = useCallback(() => {
    if (isLocked || !currentUserProlificID) return;
    if (Date.now() - lastLocalInputAtRef.current >= LOCAL_TYPING_ACTIVE_MS) return;
    const socket = ws.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_SEND_INTERVAL_MS) return;
    lastTypingSentRef.current = now;
    try {
      socket.send(JSON.stringify({ type: 'typing', senderID: currentUserProlificID }));
    } catch (e) {
      console.warn('ChatUI: Failed to send typing indicator', e);
    }
  }, [currentUserProlificID, isLocked]);

  // Pulse typing while actively editing; each keystroke resets the 5s window (resume after pause counts again).
  useEffect(() => {
    if (isLocked || !userIsActivelyTyping) return;
    sendTypingIndicator();
    const intervalId = setInterval(() => {
      if (Date.now() - lastLocalInputAtRef.current < LOCAL_TYPING_ACTIVE_MS) {
        sendTypingIndicator();
      }
    }, TYPING_SEND_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [userIsActivelyTyping, isLocked, sendTypingIndicator]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    inputValueRef.current = value;
    setInput(value);
    if (value.length > 0) {
      bumpLocalTypingActivity(false);
      sendTypingIndicator();
    } else {
      clearLocalTypingActivity('clear');
    }
  };

  const isPasteShortcut = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && key === 'v') return true;
    if (e.shiftKey && e.key === 'Insert') return true;
    return false;
  };

  const blockChatPaste = (e: React.ClipboardEvent<HTMLInputElement> | React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    notePasteAttempt();
  };

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const native = e.nativeEvent as InputEvent;
    const pasteTypes = new Set([
      'insertFromPaste',
      'insertFromPasteAsQuotation',
      'insertFromDrop',
    ]);
    if (native && pasteTypes.has(native.inputType)) {
      e.preventDefault();
      notePasteAttempt();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isPasteShortcut(e)) {
      e.preventDefault();
      notePasteAttempt();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
      return;
    }
    if (e.key === 'Tab' || e.metaKey || e.ctrlKey || e.altKey) return;
    const isTypedKey = e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete';
    if (isTypedKey || input.length > 0) {
      bumpLocalTypingActivity(isTypedKey);
    }
  };

  const handleSend = () => {
    if (!currentUserProlificID) {
        console.error("ChatUI: Cannot send message - User ID (prop) not set.");
        return;
    }
    if (input.trim() === '') return;

    const composition = compositionRef.current;
    const sentAtMs = Date.now();
    const userMessage: MessagePayload = {
      content: input,
      senderID: currentUserProlificID,
      messageID: new Date().toISOString() + Math.random(),
      timestamp: new Date(sentAtMs).toISOString(),
      typingStartedAt: composition
        ? new Date(composition.startedAtMs).toISOString()
        : new Date(sentAtMs).toISOString(),
      typingEndedAt: new Date(sentAtMs).toISOString(),
      typingDurationMs: composition ? Math.max(0, sentAtMs - composition.startedAtMs) : 0,
      keystrokeCount: composition ? composition.keystrokeCount : 0,
      pasteAttemptCount: composition ? composition.pasteAttemptCount : 0,
    };

    const messageJson = JSON.stringify(userMessage);
    flushTypingBout('send', { messageID: userMessage.messageID, draftLength: input.length });
    compositionRef.current = null;
    inputValueRef.current = '';
    resetLocalTypingIndicator();
    setInput('');

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      setChatItems((prev) => [
        ...prev,
        { text: userMessage.content, sender: 'user', id: userMessage.messageID },
      ]);
      ws.current.send(messageJson);
    } else {
      console.warn(`ChatUI: WebSocket not connected (state: ${ws.current?.readyState}). Queuing message and reconnecting.`);
      pendingMessagesRef.current.push(messageJson);

      setChatItems((prev) => [
        ...prev,
        { text: userMessage.content, sender: 'user', id: userMessage.messageID },
      ]);

      if (connectionStatus !== 'reconnecting' && connectionStatus !== 'connecting') {
        reconnectAttemptRef.current = 0;
        connect();
      }
    }
  };

  const returnMessageSender = (message: MessagePayload): 'user' | 'other' => {
    return message.senderID === currentUserProlificID ? 'user' : 'other';
  };


  if (!currentUserProlificID) {
    return (
        <div className="chat-panel-container">
            <div className="chat-panel-header">Instant Messaging</div>
            <div className="chat-messages-area" style={{padding: '20px', display: 'flex', alignItems:'center', justifyContent:'center', color: '#666'}}>
                Discussion unavailable: User ID not provided to component.
            </div>
        </div>
    );
  }

  const showReconnecting = connectionStatus === 'reconnecting' || connectionStatus === 'connecting';

  return (
    <div className={`chat-panel-container${isDebateLayout ? ' chat-panel-container--debate-wide' : ''}`}>
      <div className="chat-panel-header">
        <div className={`chat-header-content ${!isLocked ? 'with-indicator' : ''}`}>
          <span>Instant Messaging</span>
          {!isLocked && (
            connectionStatus === 'connected' ? (
              <div className="partner-online-indicator">
                <span className="online-dot"></span>
                <span className="online-text">Partner online</span>
              </div>
            ) : showReconnecting ? (
              <div className="partner-online-indicator reconnecting-indicator">
                <span className="reconnecting-dot"></span>
                <span className="online-text">Reconnecting…</span>
              </div>
            ) : null
          )}
        </div>
      </div>

      <div className="chat-messages-area chat-messages-area-with-sticky">
        {isDebateLayout && (
          <div className="chat-engagement-notice-slot">
            <DebateEngagementNotice />
          </div>
        )}
        {/* Sticky header: theme (when chat active) or locked info – always visible at top */}
        <div className="chat-messages-sticky">
          {isLocked ? (
            <div className="chat-locked-info-box">
              <div className="chat-locked-info-content">
                <div className="chat-locked-rounds">{discussionAvailabilityLabel}</div>
                {showUpcomingDebateTheme && (
                  <div className="chat-locked-theme">
                    Discussion theme: <strong>&quot;{nextChatThemeTitle}&quot;</strong>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="chat-theme-box">
              <div className="chat-goal-section">
                <div className="chat-goal-number">🔥 Discussion</div>
                <div className="chat-goal-content chat-goal-content-left">
                  <div className="chat-goal-title">{chatUmbrellaTopic}</div>
                </div>
              </div>
              {currentSubtopic && (
                <div
                  key={subtopicRevision}
                  className={`chat-goal-subtopic-wrap${showSubtopicUpdated ? ' chat-goal-subtopic-wrap--new' : ''}`}
                >
                  {showSubtopicUpdated && (
                    <div className="chat-subtopic-new-badge" role="status" aria-live="assertive">
                      <span className="chat-subtopic-new-badge-icon" aria-hidden="true">✨</span>
                      <span className="chat-subtopic-new-badge-text">
                        New discussion angle, the topic just updated!
                      </span>
                    </div>
                  )}
                  <div className="chat-goal-subtopic-label">
                    Discuss from this perspective:
                  </div>
                  <div className={`chat-goal-subtopic${showSubtopicUpdated ? ' chat-goal-subtopic--highlight' : ''}`}>
                    {currentSubtopic}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Scrollable message list with round separators */}
        <div className="chat-messages-scroll">
          {showIdlePrompt && !isLocked && (
            <div className="chat-idle-prompt" role="status" aria-live="polite">
              Keep discussing...
            </div>
          )}
          {chatItems.map((item) =>
            !isDisplayMessage(item) ? (
              <div key={item.id} className="chat-round-separator">
                <span className="chat-round-separator-line" />
                <span className="chat-round-separator-text">Rounds {item.roundStart}–{item.roundEnd}</span>
                <span className="chat-round-separator-line" />
              </div>
            ) : (
              <div key={item.id} className={`message-bubble-wrapper ${item.sender}`}>
                {item.sender === 'other' && (
                  <div className="message-avatar-placeholder">
                    <img src={partnerAvatar} alt="" className="message-avatar-img" />
                  </div>
                )}
                <div className="message-content">{item.text}</div>
                {item.sender === 'user' && (
                  <div className="message-avatar-placeholder">
                    <img src={userAvatar} alt="" className="message-avatar-img" />
                  </div>
                )}
              </div>
            )
          )}
          {partnerIsTyping && !isLocked && (
            <div
              className="message-bubble-wrapper other message-bubble-wrapper--typing"
              aria-label="Partner is typing"
              role="status"
            >
              <div className="message-avatar-placeholder">
                <img src={partnerAvatar} alt="" className="message-avatar-img" />
              </div>
              <div className="message-content message-content--typing">
                <span className="typing-dots" aria-hidden="true">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {isLocked ? (
        <div className="chat-locked-message">
          <span className="chat-locked-message-icon">🔒</span>
          {CHAT_FIRST
            ? chatNumber == null
              ? 'Discussion has ended.'
              : 'Discussion is locked until the discussion begins.'
            : 'Discussion is locked during game rounds'}
        </div>
      ) : (
        <div className="chat-input-area">
          <input
            type="text"
            value={input}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={true}
            onChange={handleInputChange}
            onPaste={blockChatPaste}
            onDrop={blockChatPaste}
            onDragOver={(e) => e.preventDefault()}
            onBeforeInput={handleBeforeInput}
            onKeyDown={handleInputKeyDown}
            placeholder={showReconnecting ? "Reconnecting to chat…" : "Write and discuss..."}
          />
          <button onClick={handleSend}>Send</button>
        </div>
      )}
    </div>
  );
};

export default ChatUI;